import InfiniteLoader from "$root/components/InfiniteLoader";
import { useUserValue } from "$root/functional-areas/user";
import { beginSigninForEmailDomain } from "$root/functional-areas/user/oidc";
import { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Redirect } from "react-router";

/**
 * SSO deep-link entry point: `/sso/:emailDomain` starts the Cognito sign-in for that email
 * domain's pool and hands the browser to its Hosted UI.
 *
 * It exists for partner portals that link their users straight into the app. Linking at
 * Cognito instead means hand-building an authorize URL, which comes without `state` and
 * without `code_challenge` — the callback then cannot recover the PKCE verifier and the
 * sign-in dies there. Coming through here, the authorize request is built by the same
 * `UserManager` as every other sign-in (see `beginSigninForEmailDomain`).
 *
 * The URL is a public entry point, so it starts a sign-in and nothing more: an unmapped
 * domain simply lands on the default pool, exactly as that domain would at /login.
 */
export function SsoRedirect({ emailDomain, from }: { emailDomain?: string; from?: string }): React.JSX.Element {
	const history = useHistory();
	const user = useUserValue();
	const signedIn = user.signedIn;

	useEffect(() => {
		if (signedIn) {
			return;
		}
		(async () => {
			try {
				await beginSigninForEmailDomain(emailDomain ?? "", from);
			} catch (err) {
				// No session is being torn down here — the sign-in never started — so this is a
				// plain bounce to the login form, not a logout.
				console.error("SSO redirect failed", err);
				history.replace("/login");
			}
		})();
	}, [history, emailDomain, from, signedIn]);

	// Already signed in: a portal tile is a "take me in" link, not a "sign me in again" one.
	if (signedIn) {
		return <Redirect to={from || "/"} />;
	}

	return <InfiniteLoader style={{ minHeight: "100dvh" }} />;
}

export default SsoRedirect;
